const API_BASE_URL = process.env.REACT_APP_FQDN || 'http://localhost:5000';
const STRIPE_API_BASE_URL = process.env.REACT_APP_STRIPE_FQDN || 'http://localhost:8000';

let refreshPromise = null;

const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('username');
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const doTokenRefresh = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) throw new Error('No refresh token');

    const resp = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${refreshToken}` },
    });

    if (!resp.ok) throw new Error('Refresh failed');

    const { access_token } = await resp.json();
    localStorage.setItem('token', access_token);
    return access_token;
  })();

  // Settle the shared promise, then clear — callers already have the reference
  return refreshPromise.finally(() => {
    refreshPromise = null;
  });
};

const fetchWithAuth = async (endpoint, options = {}) => {
  const doFetch = (tok) =>
    fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(tok && { Authorization: `Bearer ${tok}` }),
        ...options.headers,
      },
    });

  let token = localStorage.getItem('token');
  let response = await doFetch(token);

  if (response.status === 401) {
    try {
      token = await doTokenRefresh();
    } catch {
      clearAuthAndRedirect();
      throw new Error('Session expired');
    }
    response = await doFetch(token);
  }

  // Catch 401 on retry too (e.g. token blacklisted server-side)
  if (response.status === 401) {
    clearAuthAndRedirect();
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return response.json();
};

// Auth
export const login = async (username, password) => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ msg: 'Login failed' }));
    throw new Error(error.msg || 'Login failed');
  }

  const data = await response.json();
  return data;
};

// Password reset
export const requestPasswordReset = async (email) => {
  const response = await fetch(`${API_BASE_URL}/auth/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Failed to request password reset');
  }

  return data;
};

export const resetPassword = async (token, password) => {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Failed to reset password');
  }

  return data;
};

// Dashboard

export const getCreditUsage = async (period = '30d', groupId) => {
  const params = new URLSearchParams({ period });
  if (groupId) params.set('group_id', groupId);
  const data = await fetchWithAuth(`/dashboard/credit-usage?${params}`);
  return data.results;
};

export const getGroupCredits = async () => {
  const data = await fetchWithAuth('/dashboard/check-group-credits', {
    method: 'GET',
  });
  return data.group_credits;
};

export const addUserToGroup = (userData) =>
  fetchWithAuth('/dashboard/add-user-to-group', {
    method: 'POST',
    body: JSON.stringify(userData),
  });

export const getUserGroupInfo = () => fetchWithAuth('/dashboard/user-group-info');

export const getAutoTopupSettings = () => fetchWithAuth('/dashboard/auto-topup-settings');

export const disableAutoTopup = () =>
  fetchWithAuth('/dashboard/disable-auto-topup', { method: 'POST' });

export const updateAutoTopup = (topupAmount, threshold) =>
  fetchWithAuth('/dashboard/update-auto-topup', {
    method: 'POST',
    body: JSON.stringify({ topup_amount: topupAmount, threshold }),
  });

export const listApiKeys = () => fetchWithAuth('/api-keys/list_api_keys');

export const generateApiKey = (name) =>
  fetchWithAuth('/api-keys/generate_api_key', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const revokeApiKey = (name) =>
  fetchWithAuth('/api-keys/revoke_api_key', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

// Admin APIs
export const checkAdmin = async () => {
  try {
    const data = await fetchWithAuth('/auth/check_admin');
    return data.is_admin || false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

export const getAllUsers = () => fetchWithAuth('/admin/users');
export const getAllGroups = () => fetchWithAuth('/admin/groups');
export const getUserGroups = () => fetchWithAuth('/admin/user-groups');

export const createGroup = (groupData) =>
  fetchWithAuth('/admin/add-group', {
    method: 'POST',
    body: JSON.stringify(groupData),
  });

export const addUser = (userData) =>
  fetchWithAuth('/admin/add-user', {
    method: 'POST',
    body: JSON.stringify(userData),
  });

export const addCredits = (groupId, credits) =>
  fetchWithAuth('/admin/add-credits', {
    method: 'POST',
    body: JSON.stringify({ groupId, credits }),
  });

export const updateUserGroup = (userId, groupId) =>
  fetchWithAuth('/admin/update-user-group', {
    method: 'POST',
    body: JSON.stringify({ userId, groupId }),
  });

// Products API - Admin only
export const listProducts = () => fetchWithAuth('/products/list_products');

export const createProduct = (productData) =>
  fetchWithAuth('/products/create_product', {
    method: 'POST',
    body: JSON.stringify(productData),
  });

export const updateProduct = (productId, productData) =>
  fetchWithAuth(`/products/update_product/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  });

export const deleteProduct = (productId) =>
  fetchWithAuth(`/products/delete_product/${productId}`, {
    method: 'DELETE',
  });

export const getProduct = (productId) => fetchWithAuth(`/products/get_product/${productId}`);

export const fetchWithStripeAuth = async (endpoint, options = {}) => {
  const doFetch = (tok) =>
    fetch(`${STRIPE_API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(tok && { Authorization: `Bearer ${tok}` }),
        ...options.headers,
      },
    });

  let token = localStorage.getItem('token');
  let response = await doFetch(token);

  if (response.status === 401) {
    try {
      token = await doTokenRefresh();
    } catch {
      clearAuthAndRedirect();
      throw new Error('Session expired');
    }
    response = await doFetch(token);
  }

  if (response.status === 401) {
    clearAuthAndRedirect();
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return response.json();
};

export const createPaymentIntent = (email, amountInPence) =>
  fetchWithStripeAuth('/api/create-payment-intent', {
    method: 'POST',
    body: JSON.stringify({ email, amountInPence }),
  });

export const createSetupIntent = (email, topupAmount, threshold) =>
  fetchWithStripeAuth('/api/create-setup-intent', {
    method: 'POST',
    body: JSON.stringify({ email, topupAmount, threshold }),
  });

export const getPaymentIntent = (paymentIntentId) =>
  fetchWithStripeAuth(`/api/payment-intent/${paymentIntentId}`);

export const getSetupIntent = (setupIntentId) =>
  fetchWithStripeAuth(`/api/get-setup-intent/${setupIntentId}`);

// Overview API
export const fetchOverviewData = async (period = '30d') =>
  fetchWithAuth(`/overview?period=${period}`);
